import { forwardRef } from 'react';
import { cn } from '../../utils/cn';

export interface InputProps extends React.InputHTMLAttributes<HTMLInputElement> {
  label?: string;
  error?: string;
  helperText?: string;
  leftIcon?: React.ReactNode;
  rightIcon?: React.ReactNode;
  variant?: 'default' | 'glass' | 'minimal';
}

const Input = forwardRef<HTMLInputElement, InputProps>(
  (
    {
      className,
      label,
      error,
      helperText,
      leftIcon,
      rightIcon,
      variant = 'glass',
      type = 'text',
      ...props
    },
    ref
  ) => {
    const baseStyles =
      'w-full bg-transparent outline-none transition-all placeholder:text-slate-500 text-sm';

    const variants = {
      default:
        'bg-black/30 border border-white/5 rounded-xl px-4 py-2.5 text-white focus:ring-1 focus:ring-blue-600/50 focus:border-blue-600/50',
      glass:
        'bg-black/40 border border-white/5 rounded-xl px-4 py-3 text-white focus:bg-black/60 focus:border-white/10',
      minimal:
        'border-b border-white/10 py-2 text-white focus:border-blue-500 px-0',
    };

    const inputStyles = cn(baseStyles, variants[variant], className);

    return (
      <div className="w-full">
        {label && (
          <label className="block text-[11px] font-bold uppercase tracking-widest text-blue-500 mb-1.5">
            {label}
          </label>
        )}
        <div className="relative">
          {leftIcon && (
            <div className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500">
              {leftIcon}
            </div>
          )}
          <input
            ref={ref}
            type={type}
            className={cn(
              inputStyles,
              leftIcon && 'pl-10',
              rightIcon && 'pr-10',
              error && 'border-red-500/50 focus:border-red-500 focus:ring-red-500/50'
            )}
            {...props}
          />
          {rightIcon && (
            <div className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-500">
              {rightIcon}
            </div>
          )}
        </div>
        {error && (
          <p className="mt-1.5 text-[10px] text-red-400 font-medium">{error}</p>
        )}
        {helperText && !error && (
          <p className="mt-1.5 text-[10px] text-slate-500">{helperText}</p>
        )}
      </div>
    );
  }
);

Input.displayName = 'Input';

export { Input };
